import { useEffect, useState } from 'react';
import { Link } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { Buildings, Users, Key } from '@phosphor-icons/react';
import { fetchAdminSchools, fetchAdminUsers } from '../lib/admin-users';
import { Alert } from '@/components/ui/alert';

export default function AdminUsers() {
  const [stats, setStats] = useState<{ schools: number; users: number } | null>(null);
  const [statsFailed, setStatsFailed] = useState(false);

  useEffect(() => {
    Promise.all([fetchAdminSchools(), fetchAdminUsers({ perPage: 200 })])
      .then(([s, u]) => setStats({ schools: s.length, users: u.length }))
      .catch(() => setStatsFailed(true));
  }, []);

  const navCards = [
    {
      to: '/admin/users/schools',
      icon: <Buildings size={30} />,
      title: 'Établissements',
      desc: 'Liste, création, directeur et enseignants de chaque école.',
      stat: stats ? `${stats.schools} école${stats.schools > 1 ? 's' : ''}` : null,
    },
    {
      to: '/admin/users/annuaire',
      icon: <Users size={30} />,
      title: 'Annuaire',
      desc: 'Tous les utilisateurs, leurs rôles et leurs établissements.',
      stat: stats ? `${stats.users} utilisateur${stats.users > 1 ? 's' : ''}` : null,
    },
    {
      to: '/admin/users/acces',
      icon: <Key size={30} />,
      title: 'Accès & Rôles',
      desc: 'Assigner un rôle à un compte existant ou inviter par email.',
      stat: null,
    },
  ];

  return (
    <>
      <HeaderContent width="md" showBurger backTo="/admin" backLabel="Admin">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <h1 className={HEADER_TITLE_CLASS}>Utilisateurs & Rôles</h1>
            <span className={HEADER_BADGE_CLASS}>Admin</span>
          </div>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Gestion des établissements, des comptes et des permissions de la plateforme.
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[880px] mx-auto px-4 pb-16 pt-4">

        {statsFailed && (
          <Alert variant="destructive" className="mb-4">
            Les compteurs n'ont pas pu être chargés. Les rubriques ci-dessous restent accessibles.
          </Alert>
        )}

        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
          {navCards.map((card) => (
            <Link key={card.to} to={card.to} className="no-underline group">
              <div className="flex flex-col gap-4 p-5 h-full bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] hover:border-naia-blue transition-colors">
                <div className="flex items-start justify-between">
                  <span className="text-naia-blue opacity-60 group-hover:opacity-100 transition-opacity">
                    {card.icon}
                  </span>
                  {card.stat && (
                    <span className="text-[11px] font-black text-[var(--naia-text-secondary)] tabular-nums bg-naia-sky px-2 py-0.5 rounded-full">
                      {card.stat}
                    </span>
                  )}
                </div>
                <div>
                  <div className="font-extrabold text-[16px] text-[var(--naia-text-primary)] mb-1.5 group-hover:text-naia-blue transition-colors">
                    {card.title}
                  </div>
                  <div className="text-[13px] text-[var(--naia-text-secondary)] leading-relaxed">
                    {card.desc}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>
    </>
  );
}
